import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import AdminLayout from "../../LayoutWrapper/AdminLayout";
import { useAuth } from "../../context/AuthContext";
import { getDriverIdFromSession } from "../../utils/auth_storage";
import { buildMediaUrl, formatDateForInput } from "../../utils/media_url";
import { getApihandler, putApihandler } from "../../ApiHandler";
import swal from "sweetalert";
import DirectionsCarFilledOutlinedIcon from "@mui/icons-material/DirectionsCarFilledOutlined";
import ConfirmationNumberOutlinedIcon from "@mui/icons-material/ConfirmationNumberOutlined";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import BuildOutlinedIcon from "@mui/icons-material/BuildOutlined";
import PaletteOutlinedIcon from "@mui/icons-material/PaletteOutlined";
import PolicyOutlinedIcon from "@mui/icons-material/PolicyOutlined";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import CheckIcon from "@mui/icons-material/Check";
import DocUploadHero from "../../Components/DocUploadHero";
import "../../CSS/UserLogin/index.css";
import "../../CSS/DocumentUpload/index.css";

const CHECK_ITEMS = [
  {
    key: "registration",
    label: "Registration details",
    isDone: (state) =>
      state.registrationNumber &&
      state.vehicleManufacturer &&
      state.vehicleModel &&
      state.vehicleColor &&
      state.registrationExpiryDate,
  },
  {
    key: "registrationDoc",
    label: "Registration document",
    isDone: (state) => state.hasRegistrationDocument,
  },
  {
    key: "insurance",
    label: "Insurance details",
    isDone: (state) => state.insuranceNumber && state.insuranceExpiryDate,
  },
  {
    key: "insuranceDoc",
    label: "Insurance document",
    isDone: (state) => state.hasInsuranceDocument,
  },
];

export default function CarDocument() {
  const navigate = useNavigate();
  const { session } = useAuth();
  const driverId = getDriverIdFromSession(session);

  const [registrationNumber, setRegistrationNumber] = useState("");
  const [vehicleManufacturer, setVehicleManufacturer] = useState("");
  const [vehicleModel, setVehicleModel] = useState("");
  const [vehicleColor, setVehicleColor] = useState("");
  const [registrationExpiryDate, setRegistrationExpiryDate] = useState("");
  const [registrationDocument, setRegistrationDocument] = useState(null);
  const [insuranceNumber, setInsuranceNumber] = useState("");
  const [insuranceExpiryDate, setInsuranceExpiryDate] = useState("");
  const [insuranceDocument, setInsuranceDocument] = useState(null);
  const [modelId, setModelId] = useState("");

  const [makeList, setMakeList] = useState([]);
  const [modelList, setModelList] = useState([]);
  const [colorList, setColorList] = useState([]);
  const [registrationPreview, setRegistrationPreview] = useState("");
  const [insurancePreview, setInsurancePreview] = useState("");
  const [savedVehicle, setSavedVehicle] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const today = new Date().toISOString().split("T")[0];

  const hasRegistrationDocument = Boolean(
    registrationDocument || registrationPreview
  );
  const hasInsuranceDocument = Boolean(insuranceDocument || insurancePreview);

  const formState = {
    registrationNumber,
    vehicleManufacturer,
    vehicleModel,
    vehicleColor,
    registrationExpiryDate,
    insuranceNumber,
    insuranceExpiryDate,
    hasRegistrationDocument,
    hasInsuranceDocument,
  };

  const applyVehicleData = useCallback((data) => {
    if (!data) {
      return;
    }
    const normalizedRegistrationNumber = (data.registrationNumber || "").trim();
    const normalizedManufacturer = data.vehicleManufacturer || "";
    const normalizedModel = data.vehicleModel || "";
    const normalizedColor = data.vehicleColor || "";
    const normalizedRegistrationExpiry = formatDateForInput(
      data.registrationExpiryDate
    );
    const normalizedInsuranceNumber = (data.insuranceNumber || "").trim();
    const normalizedInsuranceExpiry = formatDateForInput(
      data.insuranceExpiryDate
    );
    const registrationDocKey = data.registrationDocument || "";
    const insuranceDocKey = data.insuranceDocument || "";

    setRegistrationNumber(normalizedRegistrationNumber);
    setVehicleManufacturer(normalizedManufacturer);
    setVehicleModel(normalizedModel);
    setVehicleColor(normalizedColor);
    setRegistrationExpiryDate(normalizedRegistrationExpiry);
    setInsuranceNumber(normalizedInsuranceNumber);
    setInsuranceExpiryDate(normalizedInsuranceExpiry);
    setModelId(data.modelId || "");
    setRegistrationPreview(buildMediaUrl(registrationDocKey));
    setInsurancePreview(buildMediaUrl(insuranceDocKey));

    if (
      normalizedRegistrationNumber &&
      normalizedManufacturer &&
      normalizedModel &&
      normalizedColor &&
      normalizedRegistrationExpiry &&
      normalizedInsuranceNumber &&
      normalizedInsuranceExpiry &&
      registrationDocKey &&
      insuranceDocKey
    ) {
      setSavedVehicle({
        registrationNumber: normalizedRegistrationNumber,
        vehicleManufacturer: normalizedManufacturer,
        vehicleModel: normalizedModel,
        vehicleColor: normalizedColor,
        registrationExpiryDate: normalizedRegistrationExpiry,
        insuranceNumber: normalizedInsuranceNumber,
        insuranceExpiryDate: normalizedInsuranceExpiry,
        registrationDocumentKey: registrationDocKey,
        insuranceDocumentKey: insuranceDocKey,
        modelId: data.modelId || "",
      });
    }
  }, []);

  const fetchModelsByMake = async (manufacturerName) => {
    const makeItem = makeList.find(
      (item) => item.manufacturerName === manufacturerName
    );
    if (!makeItem?._id) {
      setModelList([]);
      return;
    }
    try {
      const response = await getApihandler(
        `/getModelByMake/${makeItem._id}/android`
      );
      if (response) {
        setModelList(response);
      }
    } catch (error) {
      console.error("Error fetching models:", error);
    }
  };

  const loadReferenceData = useCallback(async () => {
    const makeResponse = await getApihandler("/getAllMakeData/android");
    if (makeResponse) {
      setMakeList(makeResponse);
    }
    try {
      const colorResponse = await getApihandler("/getAllColorData");
      if (colorResponse?.status === 200) {
        setColorList(colorResponse.data);
      }
    } catch (error) {
      console.error("Error fetching colors:", error);
    }
    return makeResponse || [];
  }, []);

  const fetchVehicleDetails = useCallback(
    async (makes) => {
      if (!driverId) {
        setIsLoading(false);
        return;
      }
      setIsLoading(true);
      try {
        const response = await getApihandler(`/getVehicleDetails/${driverId}`);
        if (response?.status === 200 && response?.data) {
          applyVehicleData(response.data);
          const manufacturer = response.data.vehicleManufacturer;
          const makeItem = makes.find(
            (item) => item.manufacturerName === manufacturer
          );
          if (makeItem?._id) {
            const models = await getApihandler(
              `/getModelByMake/${makeItem._id}/android`
            );
            if (models) {
              setModelList(models);
            }
          }
        }
      } catch (error) {
        console.log("No vehicle data found (new driver)");
      } finally {
        setIsLoading(false);
      }
    },
    [applyVehicleData, driverId]
  );

  useEffect(() => {
    if (!driverId) {
      navigate("/driver-login", { replace: true });
      return;
    }
    const initialize = async () => {
      const makes = await loadReferenceData();
      await fetchVehicleDetails(makes);
    };
    initialize();
  }, [driverId, fetchVehicleDetails, loadReferenceData, navigate]);

  const handleManufacturerChange = async (event) => {
    const selected = event.target.value;
    setVehicleManufacturer(selected);
    setVehicleModel("");
    setModelId("");
    await fetchModelsByMake(selected);
  };

  const handleModelChange = (event) => {
    const selectedModelName = event.target.value;
    setVehicleModel(selectedModelName);
    const selectedModel = modelList.find(
      (item) => item.modelName === selectedModelName
    );
    if (selectedModel) {
      setModelId(selectedModel._id);
    }
  };

  const canFinishWithoutSave = () => {
    if (!savedVehicle) {
      return false;
    }
    if (registrationDocument || insuranceDocument) {
      return false;
    }
    return (
      registrationNumber.trim() === savedVehicle.registrationNumber &&
      vehicleManufacturer === savedVehicle.vehicleManufacturer &&
      vehicleModel === savedVehicle.vehicleModel &&
      vehicleColor === savedVehicle.vehicleColor &&
      registrationExpiryDate === savedVehicle.registrationExpiryDate &&
      insuranceNumber.trim() === savedVehicle.insuranceNumber &&
      insuranceExpiryDate === savedVehicle.insuranceExpiryDate
    );
  };

  const validateForm = () => {
    if (!registrationNumber.trim()) {
      return "Enter registration number";
    }
    if (!vehicleManufacturer) {
      return "Select vehicle manufacturer";
    }
    if (!vehicleModel) {
      return "Select vehicle model";
    }
    if (!vehicleColor) {
      return "Select vehicle color";
    }
    if (!registrationExpiryDate) {
      return "Select registration expiry date";
    }
    if (registrationExpiryDate < today) {
      return "Registration expiry date cannot be in the past";
    }
    if (!hasRegistrationDocument) {
      return "Upload registration document";
    }
    if (!insuranceNumber.trim()) {
      return "Enter insurance number";
    }
    if (!insuranceExpiryDate) {
      return "Select insurance expiry date";
    }
    if (insuranceExpiryDate < today) {
      return "Insurance expiry date cannot be in the past";
    }
    if (!hasInsuranceDocument) {
      return "Upload insurance document";
    }
    return null;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!driverId) {
      return;
    }
    const validationMessage = validateForm();
    if (validationMessage) {
      swal({ icon: "warning", text: validationMessage });
      return;
    }
    if (canFinishWithoutSave()) {
      swal({
        icon: "success",
        text: "Vehicle documents are already on file.",
      });
      navigate("/");
      return;
    }
    if (!registrationDocument && !savedVehicle?.registrationDocumentKey) {
      swal({ icon: "warning", text: "Upload registration document" });
      return;
    }
    if (!insuranceDocument && !savedVehicle?.insuranceDocumentKey) {
      swal({ icon: "warning", text: "Upload insurance document" });
      return;
    }
    setIsSubmitting(true);
    const formData = new FormData();
    formData.append("registrationNumber", registrationNumber.trim());
    formData.append("vehicleManufacturer", vehicleManufacturer);
    formData.append("vehicleModel", vehicleModel);
    formData.append("vehicleColor", vehicleColor);
    formData.append("registrationExpiryDate", registrationExpiryDate);
    formData.append("insuranceNumber", insuranceNumber.trim());
    formData.append("insuranceExpiryDate", insuranceExpiryDate);
    formData.append("modelId", modelId);
    if (registrationDocument) {
      formData.append("registrationDocument", registrationDocument);
    }
    if (insuranceDocument) {
      formData.append("insuranceDocument", insuranceDocument);
    }
    try {
      const response = await putApihandler(
        `/addVehicleDetails/${driverId}`,
        formData
      );
      if (response?.status === 200) {
        swal({
          icon: "success",
          text: "Vehicle documents saved successfully!",
        });
        navigate("/");
      } else {
        swal({
          icon: "error",
          title: "Please Try Again",
          text:
            response?.message ||
            response?.error?.response?.data?.message ||
            "Unable to save vehicle details.",
        });
      }
    } catch (error) {
      swal({
        icon: "error",
        title: "Please Try Again",
        text: "Something went wrong. Try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!driverId) {
    return null;
  }

  return (
    <AdminLayout>
      <div className="doc-upload-wrapper">
        <div className="doc-upload-steps-card">
          <p className="doc-upload-steps-label">Driver onboarding · Step 2 of 2</p>
          <div className="doc-upload-steps">
            <Link
              to="/personaldocument"
              className="doc-upload-step doc-upload-step--completed"
            >
              <span className="doc-upload-step-num">
                <CheckIcon sx={{ fontSize: 20 }} />
              </span>
              <span>Personal documents</span>
            </Link>
            <span className="doc-upload-step-line doc-upload-step-line--full" aria-hidden="true" />
            <span className="doc-upload-step doc-upload-step--active">
              <span className="doc-upload-step-num">2</span>
              <span>Vehicle documents</span>
            </span>
          </div>
        </div>

        <section className="doc-upload-page doc-upload-page--stacked">
          <DocUploadHero
            variant="banner"
            badge={
              <>
                <DirectionsCarFilledOutlinedIcon sx={{ fontSize: 18 }} />
                Driver onboarding
              </>
            }
            title="Vehicle details"
            subtitle="Register your vehicle and insurance documents to complete driver verification."
          />

          <div className="doc-upload-panel">
            <h2 className="doc-upload-card-title">Vehicle & Insurance Documents</h2>
            <p className="doc-upload-card-subtitle">
              Registration and insurance information are required. Accepted formats:
              JPG, PNG, PDF.
            </p>

            <div className="doc-upload-hint">
              <InfoOutlinedIcon />
              <span>
                Use documents that match your vehicle. Expiry dates must be valid and
                images must be clear and readable.
              </span>
            </div>

            <div className="doc-upload-checklist">
              {CHECK_ITEMS.map((item) => {
                const done = item.isDone(formState);
                return (
                  <span
                    key={item.key}
                    className={`doc-upload-check ${done ? "doc-upload-check--done" : ""}`}
                  >
                    {done ? (
                      <CheckCircleOutlineIcon fontSize="inherit" />
                    ) : (
                      <RadioButtonUncheckedIcon fontSize="inherit" />
                    )}
                    {item.label}
                  </span>
                );
              })}
            </div>

            {isLoading ? (
              <p className="doc-upload-loading">Loading your vehicle details…</p>
            ) : (
              <form className="doc-upload-form" onSubmit={handleSubmit} noValidate>
                <div className="doc-upload-section">
                  <h3 className="doc-upload-section-title">
                    <DirectionsCarFilledOutlinedIcon />
                    Vehicle registration
                  </h3>
                  <div className="doc-upload-fields">
                    <div className="user-login-field">
                      <label className="user-login-label" htmlFor="registration-number">
                        Registration Number
                      </label>
                      <div className="user-login-input-wrap">
                        <ConfirmationNumberOutlinedIcon className="user-login-input-icon" />
                        <input
                          id="registration-number"
                          className="user-login-input"
                          type="text"
                          placeholder="e.g. ABCD12345"
                          required
                          value={registrationNumber}
                          onChange={(event) =>
                            setRegistrationNumber(event.target.value)
                          }
                        />
                      </div>
                    </div>
                    <div className="doc-upload-row">
                      <div className="user-login-field">
                        <label className="user-login-label" htmlFor="vehicle-manufacturer">
                          Manufacturer
                        </label>
                        <div className="user-login-input-wrap">
                          <BuildOutlinedIcon className="user-login-input-icon" />
                          <select
                            id="vehicle-manufacturer"
                            className="user-login-input doc-upload-select"
                            required
                            value={vehicleManufacturer}
                            onChange={handleManufacturerChange}
                          >
                            <option value="">Select manufacturer</option>
                            {makeList.map((item) => (
                              <option key={item._id} value={item.manufacturerName}>
                                {item.manufacturerName}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                      <div className="user-login-field">
                        <label className="user-login-label" htmlFor="vehicle-model">
                          Model
                        </label>
                        <div className="user-login-input-wrap">
                          <DirectionsCarFilledOutlinedIcon className="user-login-input-icon" />
                          <select
                            id="vehicle-model"
                            className="user-login-input doc-upload-select"
                            required
                            value={vehicleModel}
                            onChange={handleModelChange}
                            disabled={!vehicleManufacturer}
                          >
                            <option value="">Select model</option>
                            {modelList.map((item) => (
                              <option key={item._id} value={item.modelName}>
                                {item.modelName}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>
                    <div className="doc-upload-row">
                      <div className="user-login-field">
                        <label className="user-login-label" htmlFor="vehicle-color">
                          Color
                        </label>
                        <div className="user-login-input-wrap">
                          <PaletteOutlinedIcon className="user-login-input-icon" />
                          <select
                            id="vehicle-color"
                            className="user-login-input doc-upload-select"
                            required
                            value={vehicleColor}
                            onChange={(event) => setVehicleColor(event.target.value)}
                          >
                            <option value="">Select color</option>
                            {colorList.map((item) => (
                              <option key={item._id} value={item.colorName}>
                                {item.colorName}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                      <div className="user-login-field">
                        <label
                          className="user-login-label"
                          htmlFor="registration-expiry"
                        >
                          Registration Expiry
                        </label>
                        <div className="user-login-input-wrap">
                          <CalendarTodayOutlinedIcon className="user-login-input-icon" />
                          <input
                            id="registration-expiry"
                            className="user-login-input"
                            type="date"
                            required
                            min={today}
                            value={registrationExpiryDate}
                            onChange={(event) =>
                              setRegistrationExpiryDate(event.target.value)
                            }
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="doc-upload-section">
                  <h3 className="doc-upload-section-title">
                    <CloudUploadOutlinedIcon />
                    Registration document
                  </h3>
                  <div className="doc-upload-file-row doc-upload-file-row--single">
                    <div
                      className={`doc-upload-file-zone ${
                        registrationDocument || registrationPreview
                          ? "doc-upload-file-zone--has-file"
                          : ""
                      }`}
                    >
                      <input
                        type="file"
                        name="registrationDocument"
                        accept="image/*,.pdf"
                        onChange={(event) =>
                          setRegistrationDocument(event.target.files[0] || null)
                        }
                      />
                      <UploadFileOutlinedIcon className="doc-upload-file-icon" />
                      <p className="doc-upload-file-label">Registration Document</p>
                      <p className="doc-upload-file-name">
                        {registrationDocument
                          ? registrationDocument.name
                          : registrationPreview
                            ? "Uploaded — click to replace"
                            : "Click to upload"}
                      </p>
                    </div>
                  </div>
                  {registrationPreview && (
                    <div className="doc-upload-preview">
                      <img
                        src={registrationPreview}
                        alt="Registration document"
                        className="doc-upload-preview-image"
                      />
                      <p className="doc-upload-preview-caption">
                        Current registration document on file
                      </p>
                    </div>
                  )}
                </div>

                <div className="doc-upload-section">
                  <h3 className="doc-upload-section-title">
                    <PolicyOutlinedIcon />
                    Vehicle insurance
                  </h3>
                  <div className="doc-upload-fields">
                    <div className="doc-upload-row">
                      <div className="user-login-field">
                        <label className="user-login-label" htmlFor="insurance-number">
                          Insurance Number
                        </label>
                        <div className="user-login-input-wrap">
                          <PolicyOutlinedIcon className="user-login-input-icon" />
                          <input
                            id="insurance-number"
                            className="user-login-input"
                            type="text"
                            placeholder="e.g. 9999667767"
                            required
                            value={insuranceNumber}
                            onChange={(event) =>
                              setInsuranceNumber(event.target.value)
                            }
                          />
                        </div>
                      </div>
                      <div className="user-login-field">
                        <label className="user-login-label" htmlFor="insurance-expiry">
                          Insurance Expiry
                        </label>
                        <div className="user-login-input-wrap">
                          <CalendarTodayOutlinedIcon className="user-login-input-icon" />
                          <input
                            id="insurance-expiry"
                            className="user-login-input"
                            type="date"
                            required
                            min={today}
                            value={insuranceExpiryDate}
                            onChange={(event) =>
                              setInsuranceExpiryDate(event.target.value)
                            }
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="doc-upload-section">
                  <h3 className="doc-upload-section-title">
                    <DescriptionOutlinedIcon />
                    Insurance document
                  </h3>
                  <div className="doc-upload-file-row doc-upload-file-row--single">
                    <div
                      className={`doc-upload-file-zone ${
                        insuranceDocument || insurancePreview
                          ? "doc-upload-file-zone--has-file"
                          : ""
                      }`}
                    >
                      <input
                        type="file"
                        name="insuranceDocument"
                        accept="image/*,.pdf"
                        onChange={(event) =>
                          setInsuranceDocument(event.target.files[0] || null)
                        }
                      />
                      <DescriptionOutlinedIcon className="doc-upload-file-icon" />
                      <p className="doc-upload-file-label">Insurance Document</p>
                      <p className="doc-upload-file-name">
                        {insuranceDocument
                          ? insuranceDocument.name
                          : insurancePreview
                            ? "Uploaded — click to replace"
                            : "Click to upload"}
                      </p>
                    </div>
                  </div>
                  {insurancePreview && (
                    <div className="doc-upload-preview">
                      <img
                        src={insurancePreview}
                        alt="Insurance document"
                        className="doc-upload-preview-image"
                      />
                      <p className="doc-upload-preview-caption">
                        Current insurance document on file
                      </p>
                    </div>
                  )}
                </div>

                <div className="doc-upload-actions doc-upload-actions--split">
                  <Link
                    to="/personaldocument"
                    className="doc-upload-back-btn"
                  >
                    <ArrowBackIcon fontSize="small" />
                    Back to license
                  </Link>
                  <button
                    type="submit"
                    className="doc-upload-submit"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <span className="user-login-submit-spinner" />
                    ) : (
                      <ArrowForwardIcon fontSize="small" />
                    )}
                    {isSubmitting ? "Saving…" : "Complete onboarding"}
                  </button>
                </div>
              </form>
            )}
          </div>
        </section>
      </div>
    </AdminLayout>
  );
}
